import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FaHome,
  FaCog,
  FaCogs,
  FaBarcode,
  FaDatabase,
  FaUserCircle,
  FaUsers,
  FaSignOutAlt,
  FaBuilding,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaClipboardList,
  FaIndustry,
  FaMapMarkerAlt,
  FaFileInvoice,
  FaChartBar
} from 'react-icons/fa';
import CustomScannerIcon from './CustomScannerIcon';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';


import { useConfirm } from './ConfirmModal';
import './Navigation.css';


const Navigation = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();


  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showWsSwitcher, setShowWsSwitcher] = useState(false);

  const { workspaces, activeWorkspace, switchWorkspace } = useWorkspace();

  const wsSwitcherRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wsSwitcherRef.current && !wsSwitcherRef.current.contains(e.target)) {
        setShowWsSwitcher(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Initialize body class based on collapsed state
  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    return () => document.body.classList.remove('sidebar-collapsed');
  }, [isCollapsed]);

  const { logout, getUserInfo } = useAuth();
  const user = getUserInfo();

  const allNavItems = [
    { path: '/', icon: FaHome, label: 'Dashboard' },

    { path: 'divider-inventory', icon: null, label: 'Inventory Operations', isDivider: true },
    { path: '/ims-scanner', icon: CustomScannerIcon, label: 'Smart Scanner' },
    { path: '/ims-catalog', icon: FaDatabase, label: 'Master Catalog' },
    { path: '/ims-grn', icon: FaFileInvoice, label: 'GRN & Dispatch' },
    { path: '/generator', icon: FaBarcode, label: 'Barcode Generator' },

    { path: 'divider-production', icon: null, label: 'Production Control', isDivider: true },
    { path: '/ims-workorders', icon: FaClipboardList, label: 'Work Orders' },
    { path: '/ims-production', icon: FaIndustry, label: 'Production QC' },

    { path: 'divider-tracking', icon: null, label: 'Tracking', isDivider: true },
    { path: '/ims-locations', icon: FaMapMarkerAlt, label: 'Zone Tracking' },

    { path: 'divider-analytics', icon: null, label: 'Analytics', isDivider: true },
    { path: '/ims-reports', icon: FaChartBar, label: 'Reports' },

    { path: 'divider-user-access', icon: null, label: 'User & Access', isDivider: true },
    { path: '/profile', icon: FaUserCircle, label: 'User Profile' },
    { path: '/ims-users', icon: FaUsers, label: 'Team & Access' },
    { path: '/device-manager', icon: FaCogs, label: 'Device Manager' },
    { path: '/ims-settings', icon: FaCog, label: 'IMS Settings' },
  ];

  const navItems = allNavItems.filter(item => {
    if (!user) return false;

    // Apply Workspace-level role restrictions for 'user', 'member', 'viewer'
    const wsRole = activeWorkspace?.currentUserRole;
    if (wsRole === 'user' || wsRole === 'member' || wsRole === 'viewer') {
      const restrictedPaths = ['/ims-catalog', '/ims-settings', '/ims-users'];
      if (restrictedPaths.includes(item.path)) {
        return false; // Hide these for standard users
      }
    }

    return true;
  });

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (newCollapsedState) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  };

  const handleLogout = async () => {
    const ok = await confirm({
      title: 'Logout',
      message: 'Are you sure you want to logout?',
      type: 'logout',
      confirmLabel: 'Confirm'
    });
    if (ok) {
      logout();
      navigate('/login');
    }
  };

  return (
    <nav id="tour-sidebar" className={`navigation ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="nav-header">
        <div className="nav-logo-custom">
          {!isCollapsed ? (
            <>
              <img 
                src={`${process.env.PUBLIC_URL}/static/media/robridge-logo.png`} 
                alt="RoBridge Logo" 
                className="robridge-full-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="logo-fallback" style={{ display: 'none' }}>
                <div className="-text" style={{ fontSize: '18px', fontWeight: '800', color: '#ffffff', letterSpacing: '1px' }}>ROBRIDGE</div>
              </div>
            </>
          ) : (
            <div className="logo-icon-box" style={{ background: 'transparent', boxShadow: 'none' }}>
              <span style={{ color: '#E3821E', fontSize: '24px', fontWeight: '900' }}>R</span>
            </div>
          )}
        </div>
        <button className="nav-toggle" onClick={toggleCollapse}>
          {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>
      </div>

      <div className="nav-content">
        {!isCollapsed && (
          <div className="ws-switcher" ref={wsSwitcherRef} onClick={() => setShowWsSwitcher(p => !p)}>
            <FaBuilding className="ws-icon" />
            <div className="ws-info">
              <span className="ws-label">Workspace</span>
              <span className="ws-name">{activeWorkspace ? activeWorkspace.name : 'No Workspace'}</span>
            </div>
            <FaChevronDown className={`ws-chevron ${showWsSwitcher ? 'open' : ''}`} />
            {showWsSwitcher && (
              <ul className="ws-dropdown">
                {workspaces.map(ws => (
                  <li
                    key={ws.id}
                    className={`ws-option ${activeWorkspace && activeWorkspace.id === ws.id ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); switchWorkspace(ws.id); setShowWsSwitcher(false); }}
                  >
                    <FaBuilding /> {ws.name}
                  </li>
                ))}
                <li className="ws-option create-ws" onClick={(e) => {
                  e.stopPropagation();
                  setShowWsSwitcher(false);
                  navigate('/onboarding');
                }}>
                  <FaPlus /> Create Workspace
                </li>
              </ul>
            )}
          </div>
        )}

        <ul className="nav-menu">
          {navItems.map((item) => {
            const Icon = item.icon;
            if (item.isDivider) {
              if (isCollapsed) return null;
              return (
                <li key={item.path} className="nav-divider-label">
                  <span>{item.label}</span>
                </li>
              );
            }
            return (
              <li key={item.path} className="nav-item">
                <NavLink
                  to={item.path}
                  id={item.path === '/' ? 'tour-sidebar-dashboard' : (item.path === '/ims-scanner' ? 'tour-sidebar-scanner' : (item.path === '/ims-locations' ? 'tour-sidebar-locations' : undefined))}
                  className={({ isActive }) =>
                    `nav-link ${isActive ? 'active' : ''}`
                  }
                  title={isCollapsed ? item.label : ''}
                >
                  <Icon className="nav-icon" />
                  {!isCollapsed && <span className="nav-label">{item.label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>

        <div className="nav-footer">
          <button
            className="logout-btn"
            onClick={handleLogout}
            title={isCollapsed ? 'Logout' : ''}
          >
            <FaSignOutAlt />
            {!isCollapsed && <span>Logout</span>}
          </button>

          <div className="nav-version">v1.0.0</div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
